import React, { PureComponent } from 'react';
import { Card } from 'react-md';
import { injectIntl, intlShape } from 'react-intl';
import PropTypes from 'prop-types';
import moment from 'moment';

import RangeSelector from './RangeSelector';
import GroupingSelector from './GroupingSelector';
import MonthlyOverspeeds from './MonthlyOverspeeds';
import OverspeedsBySeverity from './OverspeedsBySeverity';
import BehaviourBarChart from './BehaviourBarChart';
import * as ChartsCommon from '../../utils/charts-common';


const plusOneYear = (date) => {
  const newDate = new Date(date);
  newDate.setFullYear(newDate.getFullYear() + 1);
  return newDate;
};

export default injectIntl(class StatsTab extends PureComponent {
  static propTypes = {
    journeys: PropTypes.array.isRequired,
    intl: intlShape.isRequired,
  };
  constructor(props) {
    super(props);
    const { journeys } = props;
    const firstDate = new Date(journeys.map(j => j.startDateTime).reduce((a, b) => (a < b ? a : b), null));
    const lastDate = new Date(journeys.map(j => j.startDateTime).reduce((a, b) => (a > b ? a : b), null));
    const dataSubset = ChartsCommon.getOverspeedsDataSubset(journeys, firstDate, lastDate);
    const bahviourDataSubset = ChartsCommon.getBehavioursDataSubset(journeys, firstDate, lastDate);
    const maxDate = new Date(Math.max(plusOneYear(firstDate), lastDate));
    const bySeverity = ChartsCommon.aggregateOverspeedsBySeverity(ChartsCommon.groupByMonth(dataSubset, firstDate, maxDate));
    const behaviour = ChartsCommon.aggregateBehavioursByType(ChartsCommon.groupByMonth(bahviourDataSubset, firstDate, maxDate));

    this.state = {
      minDate: moment(firstDate),
      maxDate: moment(maxDate),
      startDate: moment(firstDate),
      endDate: moment(maxDate),
      grouping: 'MONTHLY', // WEEKLY, DAYLY, DAY_OF_WEEK, HOUR_OF_DAY
      bySeverity,
      behaviour,
      aggregatedOverspeeds: ChartsCommon.aggregateOverspeeds(bySeverity),
      overallOverspeeds: ChartsCommon.aggregateOverspeedsBySeverity({ undefined: dataSubset }),
    };
  }
  componentWillReceiveProps({ journeys }) {
    const firstDate = new Date(journeys.map(j => j.startDateTime).reduce((a, b) => (a < b ? a : b), null));
    const lastDate = new Date(journeys.map(j => j.startDateTime).reduce((a, b) => (a > b ? a : b), null));
    const maxDate = new Date(Math.max(plusOneYear(firstDate), lastDate));
    const dataSubset = ChartsCommon.getOverspeedsDataSubset(journeys, firstDate, lastDate);
    const bahviourDataSubset = ChartsCommon.getBehavioursDataSubset(journeys, firstDate, lastDate);
    const bySeverity = ChartsCommon.aggregateOverspeedsBySeverity(ChartsCommon.groupByMonth(dataSubset, firstDate, maxDate));
    const behaviour = ChartsCommon.aggregateBehavioursByType(ChartsCommon.groupByMonth(bahviourDataSubset, firstDate, maxDate));
    this.setState({
      minDate: moment(firstDate),
      maxDate: moment(maxDate),
      startDate: moment(firstDate),
      endDate: moment(maxDate),
      bySeverity,
      behaviour,
      grouping: 'MONTHLY',
      aggregatedOverspeeds: ChartsCommon.aggregateOverspeeds(bySeverity),
      overallOverspeeds: ChartsCommon.aggregateOverspeedsBySeverity({ undefined: dataSubset }),
    });
  }

  onDatesChange = ({ startDate, endDate }) => {
    this.setState({ startDate, endDate: endDate || startDate }, () => {
      this.handleGroupingChange(this.state.grouping);
    });
  }

  handleGroupingChange = (grouping) => {
    const { startDate, endDate } = this.state;
    let newEndDAte = endDate;
    const { intl } = this.props;
    const diffInDays = endDate.diff(startDate, 'days');
    let groupingFunc = () => {};
    switch (grouping) {
      case 'DAYLY':
        if (diffInDays > 30) {
          newEndDAte = moment(startDate).add(30, 'days').endOf('day');
        }
        groupingFunc = ChartsCommon.groupByDay;
        break;
      case 'WEEKLY':
        if (diffInDays > 90) {
          newEndDAte = moment(startDate).add(90, 'days').endOf('day');
        }
        groupingFunc = ChartsCommon.groupByWeek;
        break;
      case 'MONTHLY':
        groupingFunc = ChartsCommon.groupByMonth;
        break;
      case 'DAY_OF_WEEK':
        groupingFunc = ChartsCommon.groupByDayOfWeek;
        break;
      case 'HOUR_OF_DAY':
        groupingFunc = ChartsCommon.groupByHourOfDay;
        break;
      case 'GROUP_OF_HOUR_OF_DAY':
        groupingFunc = ChartsCommon.groupByGroupOfHourOfDay;
        break;
      case 'LIMIT':
        groupingFunc = ChartsCommon.groupByLimit;
        break;
      default:
    }
    const dataSubset = ChartsCommon.getOverspeedsDataSubset(this.props.journeys, startDate.toDate(), newEndDAte.toDate());
    const bahviourDataSubset = ChartsCommon.getBehavioursDataSubset(this.props.journeys, startDate.toDate(), newEndDAte.toDate());
    let bySeverity = ChartsCommon.aggregateOverspeedsBySeverity(groupingFunc(dataSubset, startDate.toDate(), newEndDAte.toDate()));
    let behaviour = ChartsCommon.aggregateBehavioursByType(groupingFunc(bahviourDataSubset, startDate.toDate(), newEndDAte.toDate()));

    if (grouping === 'DAY_OF_WEEK' && intl.locale !== 'en') {
      const mondayFirst = ([sunday, ...otherDays]) => [...otherDays, sunday];
      bySeverity = mondayFirst(bySeverity);
      behaviour = mondayFirst(behaviour);
    }
    this.setState({
      grouping,
      bySeverity,
      behaviour,
      endDate: newEndDAte,
      aggregatedOverspeeds: ChartsCommon.aggregateOverspeeds(bySeverity),
      overallOverspeeds: ChartsCommon.aggregateOverspeedsBySeverity({ undefined: dataSubset }),
    });
  }

  render() {
    const {
      grouping, bySeverity, behaviour, aggregatedOverspeeds, overallOverspeeds,
      startDate, endDate, minDate, maxDate,
    } = this.state;
    const { intl } = this.props;
    const presets = [{
      text: intl.formatMessage({ id: 'stats.period.overall' }),
      start: minDate,
      end: maxDate,
    },
    {
      text: intl.formatMessage({ id: 'stats.period.today' }),
      start: moment().utc().startOf('day'),
      end: moment().utc().endOf('day'),
    },
    {
      text: intl.formatMessage({ id: 'stats.period.yesterday' }),
      start: moment().utc().subtract(1, 'day').startOf('day'),
      end: moment().utc().subtract(1, 'day').endOf('day'),
    },
    {
      text: intl.formatMessage({ id: 'stats.period.currentWeek' }),
      start: moment().utc().startOf('isoWeek'),
      end: moment().utc().endOf('isoWeek'),
    },
    {
      text: intl.formatMessage({ id: 'stats.period.lastWeek' }),
      start: moment().utc().subtract(1, 'week').startOf('isoWeek'),
      end: moment().utc().subtract(1, 'week').endOf('isoWeek'),
    },
    {
      text: intl.formatMessage({ id: 'stats.period.currentMonth' }),
      start: moment().utc().startOf('month'),
      end: moment().utc().endOf('month'),
    },
    {
      text: intl.formatMessage({ id: 'stats.period.lastMonth' }),
      start: moment().utc().subtract(1, 'month').startOf('month'),
      end: moment().utc().subtract(1, 'month').endOf('month'),
    }];

    return (
      <Card style={{ height: 'calc(100vh - 140px - 14px)' }}>
        <div className="md-grid">
          <div className="md-cell--right">
            <RangeSelector
              startDate={startDate}
              endDate={endDate}
              presets={presets}
              onDatesChange={this.onDatesChange}
              noBorder
            />
          </div>
          <div style={{ paddingRight: '15px' }}><GroupingSelector value={grouping} onChange={this.handleGroupingChange} /></div>
        </div>
        <div className="md-grid" style={{ height: 'calc(100vh - 185px - 14px)' }}>
          <div className="md-cell--7 md-grid--stacked" style={{ height: '100%' }}>
            <h4 className="md-text-center">{intl.formatMessage({ id: 'stats.charts.totalOverspeeds.title' })}</h4>
            <div style={{ height: 'calc( 50% - 2.5rem )' }}>
              <MonthlyOverspeeds chartData={aggregatedOverspeeds} grouping={grouping} />
            </div>
            <h4 className="md-text-center">{intl.formatMessage({ id: 'stats.charts.overspeedsBySeverity.title' })}</h4>
            <div style={{ height: 'calc( 50% - 2.5rem )' }}>
              <OverspeedsBySeverity chartData={bySeverity} grouping={grouping} />
            </div>
          </div>
          <div className="md-cell--5" style={{ height: '100%' }}>
            <h4 className="md-text-center">{intl.formatMessage({ id: 'stats.charts.totalOverspeedsBySeverity.title' })}</h4>
            <div style={{ height: 'calc( 50% - 2.5rem )' }}>
              <OverspeedsBySeverity
                chartData={overallOverspeeds}
                legend
                grouping="NO_GROUPING"
                startDate={startDate.toDate()}
                endDate={endDate.toDate()}
              />
            </div>
            <h4 className="md-text-center">{intl.formatMessage({ id: 'stats.charts.behaviourChart.title' })}</h4>
            <div style={{ height: 'calc( 50% - 2.5rem )' }}>
              <BehaviourBarChart chartData={behaviour} grouping={grouping} legend />
            </div>
          </div>
        </div>
      </Card>
    );
  }
});



// WEBPACK FOOTER //
// ./src/components/Stats/index.js